/**
 * Article guide interactions:
 * - Auto TOC from H2s + continuous progress rail
 * - Explored message at end of TOC
 * - View count (eye)
 * - Local upvote/downvote
 * - Copy link
 */
(function () {
  function slugify(text) {
    return String(text || '')
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .slice(0, 80);
  }

  function uniqueId(base, used) {
    let id = base || 'section';
    let i = 2;
    while (used.has(id)) {
      id = base + '-' + i;
      i += 1;
    }
    used.add(id);
    return id;
  }

  function initToc(root) {
    const content = root.querySelector('[data-article-content]');
    const desktopList = root.querySelector('[data-toc-list]');
    const mobileList = root.querySelector('[data-toc-list-mobile]');
    const lists = [desktopList, mobileList].filter(Boolean);
    const explored = root.querySelector('[data-explored-banner]');
    const progressEl = root.querySelector('[data-toc-progress]');
    if (!content || !lists.length) return;

    const headings = Array.from(content.querySelectorAll('h2'));
    if (!headings.length) {
      root.querySelectorAll('.article-guide__toc-wrap, .article-guide__toc-mobile').forEach((el) => {
        el.hidden = true;
      });
      return;
    }

    const used = new Set();
    const items = headings.map((heading) => {
      const existing = heading.id;
      const id = uniqueId(existing || slugify(heading.textContent), used);
      heading.id = id;
      return { id, text: heading.textContent.trim(), el: heading };
    });

    lists.forEach((list) => {
      list.innerHTML = '';
      items.forEach((item) => {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.href = '#' + item.id;
        a.className = 'article-guide__toc-link';
        a.textContent = item.text;
        a.dataset.tocTarget = item.id;
        li.appendChild(a);
        list.appendChild(li);
      });
    });

    const links = root.querySelectorAll('.article-guide__toc-link');
    let activeIndex = 0;
    let maxReached = 0;

    function setActive(id) {
      const index = items.findIndex((item) => item.id === id);
      if (index < 0) return;
      activeIndex = index;
      maxReached = Math.max(maxReached, index);

      links.forEach((link) => {
        link.classList.toggle('is-active', link.dataset.tocTarget === id);
      });

      updateProgress();
    }

    function updateProgress() {
      if (!progressEl) return;
      const total = Math.max(items.length - 1, 1);
      const ratio = maxReached / total;
      progressEl.style.height = Math.round(ratio * 100) + '%';

      if (explored && maxReached >= items.length - 1) {
        explored.classList.add('is-visible');
      }
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      {
        rootMargin: '-18% 0px -62% 0px',
        threshold: [0, 0.25, 0.5, 1],
      }
    );

    items.forEach((item) => observer.observe(item.el));

    // Full explore when bottom of content is reached
    if (explored) {
      const endObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            maxReached = items.length - 1;
            if (progressEl) progressEl.style.height = '100%';
            explored.classList.add('is-visible');
            if (items.length) {
              links.forEach((link) => {
                link.classList.toggle(
                  'is-active',
                  link.dataset.tocTarget === items[items.length - 1].id
                );
              });
            }
          });
        },
        { threshold: 0.15 }
      );

      const sentinel = document.createElement('div');
      sentinel.setAttribute('aria-hidden', 'true');
      sentinel.style.height = '1px';
      content.appendChild(sentinel);
      endObserver.observe(sentinel);
    }

    // Initialize first item
    if (items[0]) setActive(items[0].id);

    root.querySelectorAll('.article-guide__toc-mobile .article-guide__toc-link').forEach((link) => {
      link.addEventListener('click', () => {
        const details = link.closest('details');
        if (details) details.open = false;
      });
    });
  }

  function initViews(root) {
    const views = root.querySelector('[data-article-views]');
    if (!views) return;

    const articleId = root.dataset.articleId || 'article';
    const countKey = 'article-guide-views:' + articleId;
    const seenKey = 'article-guide-viewed:' + articleId;
    const seed = parseInt(root.dataset.viewSeed || '0', 10) || 0;

    let count = seed;
    try {
      const stored = parseInt(localStorage.getItem(countKey) || '0', 10);
      count = Math.max(seed, stored || 0);
      if (!sessionStorage.getItem(seenKey)) {
        count += 1;
        sessionStorage.setItem(seenKey, '1');
        localStorage.setItem(countKey, String(count));
      } else if (!stored) {
        localStorage.setItem(countKey, String(count));
      }
    } catch (e) {
      count = Math.max(seed, 1);
    }

    const el = views.querySelector('[data-view-count]');
    if (el) el.textContent = String(count);
  }

  function initReactions(root) {
    const groups = root.querySelectorAll('[data-article-reactions]');
    if (!groups.length) return;

    const articleId = root.dataset.articleId || 'article';
    const storageKey = 'article-guide-reaction:' + articleId;
    const countKey = 'article-guide-upvotes:' + articleId;

    let vote = null;
    let count = 0;
    try {
      vote = localStorage.getItem(storageKey);
      count = parseInt(localStorage.getItem(countKey) || '0', 10) || 0;
    } catch (e) {
      /* ignore */
    }

    if (!localStorage.getItem(countKey)) {
      count = Math.max(count, 3 + (Number(articleId) % 40));
      try {
        localStorage.setItem(countKey, String(count));
      } catch (e) {
        /* ignore */
      }
    }

    function render() {
      groups.forEach((group) => {
        group.querySelectorAll('[data-reaction]').forEach((btn) => {
          const type = btn.dataset.reaction;
          const active = vote === type;
          btn.classList.toggle('is-active', active);
          btn.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        group.querySelectorAll('[data-reaction-count]').forEach((el) => {
          el.textContent = String(count);
        });
      });
    }

    function persist() {
      try {
        if (vote) localStorage.setItem(storageKey, vote);
        else localStorage.removeItem(storageKey);
        localStorage.setItem(countKey, String(count));
      } catch (e) {
        /* ignore */
      }
    }

    groups.forEach((group) => {
      group.addEventListener('click', (event) => {
        const btn = event.target.closest('[data-reaction]');
        if (!btn) return;
        const type = btn.dataset.reaction;
        const prev = vote;

        if (prev === type) {
          vote = null;
          if (type === 'up') count = Math.max(0, count - 1);
        } else {
          if (prev === 'up') count = Math.max(0, count - 1);
          vote = type;
          if (type === 'up') count += 1;
        }

        persist();
        render();
      });
    });

    render();
  }

  function initCopy(root) {
    root.querySelectorAll('[data-copy-link]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const url = btn.getAttribute('data-copy-link');
        try {
          await navigator.clipboard.writeText(url);
          btn.setAttribute('aria-label', 'Link copied');
          setTimeout(() => btn.setAttribute('aria-label', 'Copy link'), 1600);
        } catch (e) {
          const input = document.createElement('input');
          input.value = url;
          document.body.appendChild(input);
          input.select();
          document.execCommand('copy');
          input.remove();
        }
      });
    });
  }

  function polishContent(root) {
    const content = root.querySelector('[data-article-content]');
    if (!content) return;

    // Remove default CMS divider lines
    content.querySelectorAll('hr').forEach((hr) => hr.remove());

    // Remove links from headings (keep the title text only)
    content.querySelectorAll('h1 a, h2 a, h3 a, h4 a, h5 a, h6 a').forEach((link) => {
      link.replaceWith(document.createTextNode(link.textContent));
    });

    // Drop the pointing-hand emoji that used to introduce call-to-action buttons
    content.querySelectorAll('p, li').forEach((el) => {
      const html = el.innerHTML;
      const cleaned = html.replace(/\s*👉\s*/gu, ' ').replace(/^\s+/, '');
      if (cleaned !== html) el.innerHTML = cleaned;
    });

    // Remove the link-card icons the editor adds inside links
    content.querySelectorAll('a svg').forEach((svg) => {
      const holder = svg.closest('span');
      (holder && holder.closest('a') ? holder : svg).remove();
    });

    // Make plain-text URLs clickable as ordinary underlined links
    const plainUrlPattern = /https?:\/\/[^\s<>"']+/gi;
    content.querySelectorAll('p, li').forEach((el) => {
      const hasUrl = plainUrlPattern.test(el.textContent);
      plainUrlPattern.lastIndex = 0;
      if (!hasUrl || el.querySelector('a')) return;
      el.innerHTML = el.innerHTML.replace(plainUrlPattern, (url) => {
        const clean = url.replace(/[.,;:!?)]+$/, '');
        return '<a href="' + clean + '">' + clean + '</a>' + url.slice(clean.length);
      });
    });
  }

  function init(root) {
    polishContent(root);
    initToc(root);
    initViews(root);
    initReactions(root);
    initCopy(root);
  }

  function boot() {
    document.querySelectorAll('[data-article-guide]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const root = event.target.querySelector('[data-article-guide]');
    if (root) init(root);
  });
})();
